import { ICON__sitemap } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_grid2d, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { HOOK_GRID2D_CELL__button_size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__button_size';
import { REMOTE__generation_create } from '@/state/generation/entity/REMOTE__generation_create';
import { assert_equal, assert_instanceof } from '@shared/asserts/utils_asserts';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO_REQUEST__generation_create } from '@shared/dto/generation/_requests/DTO_REQUEST__generation_create';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__base } from '@shared/dto/generation/graph_invoke/_fields/_primitives/_base/FIELD_PRIMITIVE__base';
import { DTO_GRAPH__instance } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__instance';
import { DTO__grid2d_generation } from '@shared/dto/grid2d/DTO__grid2d_generation';
import { DTO__grid2d_cell_generation } from '@shared/dto/grid2d_cell/DTO__grid2d_cell_generation';
import { ENUM__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { useMemo, useState } from 'react';

interface Props {
  cell: DTO__grid2d_cell_generation;
  wrapper_class_name: string;
}

export function BUTTON_GRID2D_IMAGE__create_child_generation({ cell, wrapper_class_name }: Props) {
  /*
   *
   */
  const dispatch = use_dispatch();
  const { toast } = useToast();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user', 'workspace_current']);
  const { grids2d_map } = use_slice_grid2d(['grids2d_map']);

  /*
   * STATE
   */
  const grid2d_cell_button_size = HOOK_GRID2D_CELL__button_size(user_settings.ui_grid2d_cell_size);

  const [is_loading, set_is_loading] = useState(false);

  const grid2d_has_more_than_one_cell = useMemo(() => get_grid2d_has_more_than_one_cell(), [generation_current, grids2d_map]);

  /*
   * EVENTS
   */

  async function onClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();

    if (is_loading) return;

    set_is_loading(true);

    let graph_new = generation_current.tool.graph.clone();

    let field_row = graph_new.grid2d__get_field_row();
    grid2d_dimension__set_value(cell.field_row, field_row, graph_new);

    let field_column = graph_new.grid2d__get_field_column();
    grid2d_dimension__set_value(cell.field_column, field_column, graph_new);

    graph_new.grid2d__dimensions_reset_to_stub();
    graph_new.set_graph_id(ENUM__string_id_empty.OBJECT_ID);
    graph_new.hash_clear();

    let request = new DTO_REQUEST__generation_create();
    request.generation_id_previous = generation_current._id;
    request.graph = graph_new;
    request.user_task_id = generation_current.user_task_id;

    let action_result = await dispatch(REMOTE__generation_create(request));

    if (typeof action_result.payload == 'string') {
      toast({ title: 'Child generation created' });
    }

    set_is_loading(false);
  }

  /*
   * METHODS
   */

  function grid2d_dimension__set_value(field_from: FIELD__base, field_to: FIELD__base, graph: DTO_GRAPH__instance) {
    assert_instanceof(field_from, FIELD_PRIMITIVE__base);
    assert_instanceof(field_to, FIELD_PRIMITIVE__base);

    assert_equal(field_from.constructor, field_to.constructor);

    if (field_from.get_value() == field_to.get_value()) return;

    field_to.set_value(field_from.get_value());

    graph.field__replace(field_from);
  }

  function get_grid2d_has_more_than_one_cell() {
    if (generation_current.is_stub()) return false;
    if (!grids2d_map.has(generation_current._id)) return false;

    let grid2d = grids2d_map.get_or_assert(generation_current._id);
    assert_instanceof(grid2d, DTO__grid2d_generation);

    return grid2d.get_generation_chunk().cells_list.length > 1;
  }

  /*
   * RENDERS
   */

  if (generation_current.is_stub()) return null;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return null;
  if (cell.hash_sha256_content.is_empty_hash_sha256()) return null;
  if (!grid2d_has_more_than_one_cell) return null;

  return (
    <div className={wrapper_class_name}>
      <UI_PRIMITIVE__button_with_icon_animated
        Icon={ICON__sitemap}
        disabled={is_loading}
        iconClassNameBase={`!text-component-item-foreground !size-5 !duration-300
             ${user_settings.ui_grid2d_cell_size === ENUM__grid2d_cell_size.L ? '!size-7' : ''}`}
        iconClassNameHovered="!text-bar-foreground"
        variant="no-border-on-component"
        {...{ onClick }}
        className={`p-0 rounded-lg  bg-bar cursor-pointer}
          ${grid2d_cell_button_size}`}
      />
    </div>
  );
}
